import React, { Fragment, useMemo } from 'react';

import { graphql, useFragment } from 'react-relay';

import UFOSegment from '@atlaskit/react-ufo/segment';
import { ModalContextProvider } from '@atlassian/entry-points/modal-context-provider';
import { BFFConsumerEnum, BFFProvider } from '@atlassian/navigation-bff-data-provider';
import type { ObjectListFlyoutItem } from '@atlassian/navigation-common/types';
import { NavEmptyState } from '@atlassian/navigation-common/ui/nav-empty-state';
import { NavErrorBoundary } from '@atlassian/navigation-error-boundary';

import { withSideNavigationAnalyticsContext } from '../common/analytics/context';
import type { GlobalSideNavigationProps } from '../common/types';
import { AsyncIntlProvider } from '../common/utils/locale';
import { ItemsAndShortcutsProvider } from '../controllers/items-and-shortcuts-provider';
import { OptionsProvider } from '../controllers/options-provider';
import { PeekMenuIdProviders } from '../controllers/peek-menu-id-providers';

import type { GlobalSideNavigation$key } from './__generated__/GlobalSideNavigation.graphql';
import { GLOBAL_SIDE_NAV_UFO_INTERACTION_NAME } from './constants';
import { SideNavigationLayout } from './layout';
import { messages } from './messages';
import { LocalStore } from './side-nav/LocalStore';
import { NetworkMigrationStore } from './side-nav/NetworkMigrationStore';
import { NetworkStore } from './side-nav/NetworkStore';
import { SideNav } from './side-nav/SideNav';

// Wrap the component in Reactact.memo`, which performs a shallow (referential)
// comparison of all props and bails out when they are all equal;
// The default `React.memo` shallow comparator is exactly what we want here
// (the callers already memoise `items`, `shortcuts`, `forYou`,
// `networkStorageParams`, etc.), so no custom comparator is needed.
const MemoizedRawGlobalSideNavigation: React.MemoExoticComponent<
	(props: GlobalSideNavigationProps) => React.JSX.Element
> = React.memo(RawGlobalSideNavigation);

export const GlobalSideNavigation: React.ComponentType<
	GlobalSideNavigationProps<ObjectListFlyoutItem>
> = withSideNavigationAnalyticsContext(MemoizedRawGlobalSideNavigation);

export default MemoizedRawGlobalSideNavigation; // This is only needed to support the Atlaskit docs. See https://hello.atlassian.net/wiki/spaces/navx/pages/5305993008/Platform+Guide#Atlaskit-%3CProps%3E-component-not-rendering

/**
 * Reads the customization data that GlobalRoot loaded on our behalf, and merges it
 * into `networkStorageParams` so everything downstream (OptionsProvider, the network
 * stores) reads it from the same place regardless of how GSN was mounted.
 *
 * This lives in its own component because `useFragment` requires a
 * `RelayEnvironmentProvider` ancestor — even when passed a null key. GSN must keep
 * working without any Relay environment for `local` customization, so the hook can
 * only run once a `queryRef` is present. A `queryRef` can only have been produced by
 * a Relay query, so its presence guarantees the environment exists.
 *
 * NOTE: `...useNetworkSideNavUserData` supplies the OLD record read by the
 * `network-migration` store. It is spread here — gated on `$fetchOldEntityRecord`,
 * exactly as `GlobalSideNavigationAGGRelayConnection` does — so that both ways of
 * supplying `networkStorageParams.queryRef` produce the same data shape and
 * `network-migration` works identically on either path. The cost is that every
 * GlobalRoot consumer's query must declare `$entityAriUserSideNavSettings` and
 * `$fetchOldEntityRecord`; consumers that do not migrate pass `false` and an empty
 * ARI, which skips the extra selection entirely.
 */
function GlobalSideNavigationFromGlobalRoot(
	props: GlobalSideNavigationProps & {
		queryRef: NonNullable<GlobalSideNavigationProps['queryRef']>;
	},
): React.JSX.Element {
	const { networkStorageParams, queryRef } = props;

	// The data read here is handed to the network stores via `networkStorageParams`
	// rather than consumed in this module, mirroring
	// `GlobalSideNavigationAGGRelayConnection`.
	/* eslint-disable @atlassian/relay/must-colocate-fragment-spreads */
	/* eslint-disable @atlassian/relay/unused-fields */
	const globalRootData = useFragment<GlobalSideNavigation$key>(
		graphql`
			fragment GlobalSideNavigation on Query {
				...useNetworkSideNavUserData
					@dangerously_unaliased_fixme
					@include(if: $fetchOldEntityRecord)
				...useGlobalShortcutsDataRelay
					@dangerously_unaliased_fixme
					@include(if: $fetchGlobalShortcutsAvailableProducts)
				navPlatL1MenuData: settings_navigationCustomisation(
					entityAri: $navPlatMenuDataEntityAri
					ownerAri: $ownerAri
				) {
					...useNavSettingsService
				}
				navPlatSidebarData: settings_navigationCustomisation(
					entityAri: $navPlatSidebarDataEntityAri
					ownerAri: $ownerAri
				) {
					...useNavSettingsService
				}
			}
		`,
		queryRef,
	);

	const effectiveNetworkStorageParams = useMemo(() => {
		if (!networkStorageParams) {
			return networkStorageParams;
		}
		// `globalRootData` is non-nullable: this component is only rendered behind a
		// truthy `queryRef` check, so the fragment key is always present and Relay
		// always returns data. Do not "simplify" this into a single component that
		// calls `useFragment` with a nullable key — that would run the hook without a
		// `RelayEnvironmentProvider` for local-storage consumers, which throws.
		return { ...networkStorageParams, queryRef: globalRootData };
	}, [globalRootData, networkStorageParams]);

	return (
		<GlobalSideNavigationInner {...props} networkStorageParams={effectiveNetworkStorageParams} />
	);
}

/**
 * The presence of a `queryRef` is what selects between the two paths: it can only have
 * come from a GlobalRoot query, so it doubles as the signal that GSN should read its
 * customization data from that query rather than from
 * `GlobalSideNavigationAGGRelayConnection` or local storage.
 *
 * This selects between two component types rather than branching on a hook, so the
 * Relay read above is never reached by consumers who do not supply a `queryRef`.
 * A `queryRef` that changes between defined and undefined would remount the subtree;
 * in practice it is either absent for the lifetime of the component (local storage and
 * connection-component consumers) or supplied from a suspended query that has already
 * resolved.
 */
function RawGlobalSideNavigation(props: GlobalSideNavigationProps): React.JSX.Element {
	return props.queryRef ? (
		<GlobalSideNavigationFromGlobalRoot {...props} queryRef={props.queryRef} />
	) : (
		<GlobalSideNavigationInner {...props} />
	);
}

function GlobalSideNavigationInner(props: GlobalSideNavigationProps): React.JSX.Element {
	const {
		app,
		appLabel,
		cloudId,
		customizationStore = 'local',
		defaultMenuVisibility,
		disableMenuCustomization = false,
		forYou,
		growthItemShortcuts,
		intlMode = 'async',
		isAnonymous = false,
		isShortcutsStatic,
		items,
		networkStorageParams,
		peekMenuId,
		queryRef,
		shortcuts,
		userId,
		...innerProps
	} = props;
	const IntlWrapper = intlMode === 'inherit' ? Fragment : AsyncIntlProvider;

	// If user is anon, disable menu customization and use local store
	const effectiveDisableMenuCustomization = disableMenuCustomization || isAnonymous;
	const effectiveCustomizationStore = isAnonymous ? 'local' : customizationStore;

	const Store = useMemo(() => {
		switch (effectiveCustomizationStore) {
			case 'local':
				return LocalStore;
			case 'network-migration':
				return NetworkMigrationStore;
			default:
				return NetworkStore;
		}
	}, [effectiveCustomizationStore]);

	return (
		<UFOSegment name={GLOBAL_SIDE_NAV_UFO_INTERACTION_NAME}>
			<IntlWrapper>
				<ModalContextProvider>
					<NavErrorBoundary
						errorFallbackUI={({ header, description }) => (
							<SideNavigationLayout>
								<NavEmptyState header={header} description={description} />
							</SideNavigationLayout>
						)}
						errorMessages={messages}
						// `location` is no longer used for error-message lookup; it is retained
						// purely to tag the nav-boundary context for Sentry/analytics so errors
						// caught at the top of the side nav remain attributable to this surface.
						location="global-side-navigation"
						sentryConfig={{ product: app }}
						ufoInteractionName={GLOBAL_SIDE_NAV_UFO_INTERACTION_NAME}
					>
						<BFFProvider consumer={BFFConsumerEnum.GLOBAL_SIDE_NAVIGATION}>
							<OptionsProvider
								app={app}
								appLabel={appLabel}
								cloudId={cloudId}
								customizationStore={effectiveCustomizationStore}
								defaultMenuVisibility={defaultMenuVisibility}
								disableMenuCustomization={effectiveDisableMenuCustomization}
								hasBottomElement={!!innerProps?.bottomElem}
								intlMode={intlMode}
								isShortcutsStatic={isShortcutsStatic || isAnonymous}
								l1DragAndDropEnabled={!effectiveDisableMenuCustomization}
								networkStorageParams={networkStorageParams}
								userId={userId}
							>
								<ItemsAndShortcutsProvider
									forYou={isAnonymous ? {} : forYou}
									growthItemShortcuts={growthItemShortcuts}
									items={isAnonymous && props.anonymousItems ? props.anonymousItems : items}
									rawShortcuts={shortcuts}
								>
									<PeekMenuIdProviders value={peekMenuId}>
										<Store>
											<SideNav {...innerProps} />
										</Store>
									</PeekMenuIdProviders>
								</ItemsAndShortcutsProvider>
							</OptionsProvider>
						</BFFProvider>
					</NavErrorBoundary>
				</ModalContextProvider>
			</IntlWrapper>
		</UFOSegment>
	);
}
